// src/services/aeo/clientDocsMock.ts
//
// The documents sample store: a fake server for phase 1 while the backend
// does not exist. It serves only on a local fixtures run: the dev server, the
// docs switch VITE_ENABLE_OURA_DOCS_LIVE not "true" and neither
// VITE_ENABLE_OURA_LIVE_API. A build never serves it. On staging and production,
// with the docs switch still off, clientDocs.ts answers "not available yet"
// instead, so no fixture client reaches a live screen. DELETE once the
// documents routes are live.
//
// It stores raw Drive-shaped data (files, permissions, comments, replies) and
// COMPUTES every state on each read with the rules in clientDocsRules.ts, the
// way the backend will. So a Pick a code, a reply-by change or a simulated
// client reply recomputes the board, the drawer, All clients and the
// merchant's list at once.
//
// Fixture times are stored relative to today ("@wd:-8T10:00" is 8 Malaysian
// working days ago at 10:00) and resolved on every read, so a late doc stays
// late and a fresh one stays fresh whenever the preview is opened.
//
// Dev tools, in the browser console (sample store only):
//   window.__ouraResetDocs()                 reseed the fixtures
//   localStorage.__oura_docs_mode__ = "slow" every call waits 3 s ("error": every call fails,
//                                            the merchant's client page included)
//   window.__ouraDocsMode = "error"          the same, for this tab only, no reload needed
//   window.__ouraDocsSim.clientReply(id)     the client answers the first question waiting on them
//   window.__ouraDocsSim.ourReply(id)        we answer the first thread that needs us
//   window.__ouraDocsSim.approve(id)         the client resolves the first open question
//   window.__ouraDocsSim.share(id)           share the doc to the client's email, comment-only
//   window.__ouraDocsSim.unshare(id)         take the client's access away
//   window.__ouraDocsSim.list("nyonya-table") the docs and their states
//
// Phase 2 (the write sample, clientDocsWriteMock.ts) writes into THIS store,
// never a copy: it registers a seed extension (`registerDocsSeedExtension`)
// that adds its Drive-shaped fixtures after every seed, and mutates files,
// threads and replies through `docsStoreApi`. The B26 rules (posts on the
// client's behalf, question records, per-code states, the amber flag) are
// computed here on every read, the way the backend will.

import { sampleActor } from "./sampleActor";
import type {
    AcrossClient,
    AcrossRow,
    BusinessDocuments,
    CodeRow,
    CodeSource,
    CodeState,
    DocAuthor,
    DocFlag,
    DocPost,
    DocPostSummary,
    DocSharedWith,
    DocState,
    DocThread,
    DocsAcrossClients,
    DocsConnection,
    DocsErrorCode,
    DocsFailReason,
    DocsFailure,
    DocsFolder,
    DocsFolderCreated,
    DocsResult,
    DocumentDetail,
    LinkRole,
    MerchantDocState,
    MerchantDocument,
    PickCodeIn,
    StaffDocument,
    ThreadState,
} from "./clientDocsTypes";
import type { AnswerVia, ClientAnswerKind, QuestionKind } from "./clientDocsWriteTypes";
import { DOCS_WRITE_ERROR_MESSAGE } from "./clientDocsWriteTypes";
import {
    BASE_CODE_RE,
    CODE_RE,
    OURA_ACCOUNT,
    OUTLET_INVALID_TEXT,
    addDays,
    codeChipsOf,
    codeExpectation,
    defaultReplyBy,
    docStateForCode,
    docStateOf,
    fileTypeOf,
    isOurEmail,
    docDateOf,
    joinCodes,
    medianWhole,
    myDate,
    myInstant,
    outletOk,
    outletsInGroup,
    parseDocName,
    parseDriveFolderLink,
    parseTitleCodes,
    planDay1,
    resolveCarriesAgreement,
    shiftWorkingDays,
    threadCodePrefix,
    unreadCodeGroup,
    threadKind,
    whoSpokeFor,
    workingDaysBetween,
    type ClientWord,
} from "./clientDocsRules";

/* ------------------------------------------------------------------------- *
 * Dev mode: slow, error or normal
 * ------------------------------------------------------------------------- */

export type DocsDevMode = "slow" | "error" | "normal";

const MODE_KEY = "__oura_docs_mode__";

/** The dev switch: `window.__ouraDocsMode` wins over localStorage `__oura_docs_mode__`. */
export function docsDevMode(): DocsDevMode {
    let raw: unknown = null;
    try {
        raw = (window as unknown as { __ouraDocsMode?: unknown }).__ouraDocsMode ?? window.localStorage.getItem(MODE_KEY);
    } catch {
        raw = null;
    }
    return raw === "slow" ? "slow" : raw === "error" ? "error" : "normal";
}

/** How long a sample call takes in the current mode. */
export function docsMockDelayMs(normal = 160): number {
    return docsDevMode() === "slow" ? 3000 : normal;
}

/**
 * Whether this store answers the documents calls: only on a local fixtures
 * run (the dev server, Oura on fixtures). Anywhere else with the docs switch
 * off, a build included, the calls answer `notServed` and the merchant page
 * carries no `documents`.
 */
export const DOCS_SAMPLE_SERVES =
    import.meta.env.DEV &&
    import.meta.env.VITE_ENABLE_OURA_DOCS_LIVE !== "true" &&
    import.meta.env.VITE_ENABLE_OURA_LIVE_API !== "true";

/* ------------------------------------------------------------------------- *
 * Failures
 * ------------------------------------------------------------------------- */

export const DOCS_ERROR_MESSAGE: Record<DocsErrorCode, string> = {
    folder_url_invalid: "That isn't a Google Drive folder link.",
    folder_not_readable: "Oura's Google account can't open this folder. Give it edit access.",
    folder_linked_elsewhere: "This folder is already linked to another client.",
    already_linked: "This client already has a Drive folder linked.",
    ambiguous_folder: "More than one folder has this name in oura Project Folders. Open the one to use and paste its link below.",
    projects_folder_missing: "oura Project Folders is missing from Oura's Google Drive. Ask the team to make it.",
    docs_folder_busy: "Oura is already making this client's folder. Wait a moment, then reload the card.",
    docs_not_configured: "Oura's Google account isn't set up yet.",
    drive_unavailable: "Google Drive did not answer. Try again in a minute.",
    unknown_code: "Oura doesn't know that code.",
    outlet_invalid: "An outlet is 2 to 4 capital letters, like MK.",
    codes_in_name: "The file name already has codes, so Oura files it by those.",
    reply_by_in_past: "Pick today or a later date.",
    note_required: "Say what you'd like changed.",
    note_too_long: "Keep it to 2,000 characters or fewer.",
    file_not_yours: "That file belongs to another business.",
    // The three upload refusals. Each is something the person can act on, which
    // is the whole reason they must not read as "Could not reach the server".
    upload_empty: "That file is empty.",
    upload_too_large: "Uploads are capped at 20 MB.",
    upload_type_not_allowed: "Only PDF, PNG, JPEG or WebP files can be uploaded.",
    reply_required: "Write a reply first.",
    small_change_already_filed: "This month's small change is already filed.",
    confirm_name_mismatch: "Type the file's name exactly to delete it.",
    not_file_owner: "Oura's Google account doesn't own this file, so it can't move it to the trash. Delete it in Google Drive.",
    file_not_found_in_drive: "Google can't find this file, so nothing was deleted.",
    custom_type_invalid: "A doc type name uses letters, digits, spaces and & ' - . , only.",
    code_not_in_plan: "That code is not in this client's plan.",
    kind_not_for_custom: "A custom doc type has no template or skill. Make a blank Google Doc, link one, or convert a Word file.",
    kind_not_for_type: "A blank Google Doc is for a custom doc type.",
    outlet_unknown: "This business has no outlet by that name.",
    name_date_in_future: "The date in the name can't be after today.",
    done_on_future: "The day the work was done cannot be after today.",
    ...DOCS_WRITE_ERROR_MESSAGE,
};

const REASON_MESSAGE: Record<Exclude<DocsFailReason, "rejected">, string> = {
    network: "Could not reach the server.",
    unauthorised: "Your session has ended. Sign in again.",
    forbidden: "This account can't see this.",
    notFound: "Not found.",
    notServed: "This is not available yet.",
};

/** A failure in the shape every documents call answers with. */
export function docsFailure(
    reason: DocsFailReason,
    opts: { code?: DocsErrorCode | null; status?: number | null; message?: string | null; badCodes?: string[] | null; linkedTo?: DocsFailure["linkedTo"]; existingId?: string | null; folders?: DocsFailure["folders"] } = {},
): DocsFailure {
    const code = opts.code ?? null;
    const message = opts.message || (code ? DOCS_ERROR_MESSAGE[code] : reason === "rejected" ? "The server refused this." : REASON_MESSAGE[reason]);
    return {
        ok: false,
        reason,
        code,
        status: opts.status ?? null,
        message,
        badCodes: opts.badCodes ?? null,
        linkedTo: opts.linkedTo ?? null,
        existingId: opts.existingId ?? null,
        folders: opts.folders ?? null,
    };
}

/* ------------------------------------------------------------------------- *
 * The store
 * ------------------------------------------------------------------------- */

/**
 * An ISO instant, or a time relative to now, resolved on every read:
 * "@wd:-8T10:15" (8 working days ago, 10:15 Malaysian time), "@min:-4",
 * "@h:-3", "@cal:-44" (a date 44 calendar days ago) and "@sync" (the last
 * 5-minute sync).
 */
type Stamp = string;

export interface MockAuthor { email: string; name: string }
/**
 * A comment reply as Drive holds it. The optional fields are Oura's own
 * records about a post it wrote (phase 2), which the backend keeps beside
 * the Drive ids: where it was written, whom it speaks for (B26), how the
 * client's words arrived, who logged or signed it.
 */
export interface MockReply {
    id: string;
    by: MockAuthor;
    text: string;
    at: Stamp;
    action: "reply" | "resolve" | "reopen";
    where?: "doc" | "oura";
    onBehalfOf?: MockAuthor | null;
    via?: AnswerVia | null;
    answer?: ClientAnswerKind | null;
    loggedBy?: string | null;
    saidAt?: string | null;
    signedBy?: string | null;
    editedAt?: string | null;
    file?: { name: string; url: string } | null;
    /** On a resolve: the doc's version it was approved in, stamped when that version's PDF is filed (B23). */
    docVersion?: string | null;
}
/** Oura's record of a question it posted as this comment (B15, B26). */
export interface MockQuestionRecord { id: string; kind: QuestionKind; code: string | null; options: string[]; number: number | null; text: string }
export interface MockThread {
    id: string;
    by: MockAuthor;
    text: string;
    quoted: string | null;
    at: Stamp;
    replies: MockReply[];
    question?: MockQuestionRecord | null;
    /** A question written in the doc by hand (no record) that wants one kind of answer, such as a file. */
    askKind?: QuestionKind | null;
}
export interface MockPermission {
    type: "user" | "group" | "anyone";
    email: string | null;
    name: string | null;
    role: "reader" | "commenter" | "writer" | "owner";
}
export interface MockFile {
    id: string;
    driveFileId: string;
    businessId: string;
    folderId: string;
    /** May carry "{d}" ("18 Sep 2026"), "{D}" ("18 September 2026") or "{S}" ("18 September 2026", a 2-digit day), filled from `nameDate`. */
    name: string;
    nameDate: Stamp | null;
    pageTitle: string | null;
    mimeType: string;
    owner: string | null;
    createdAt: Stamp;
    modifiedAt: Stamp;
    trashed: boolean;
    permissions: MockPermission[];
    /** When Oura first saw it shared with the client. */
    sharedAt: Stamp | null;
    pick: { codes: string[]; outlet: string | null } | null;
    notClientDoc: boolean;
    /** A staff reply-by, YYYY-MM-DD. */
    replyBy: string | null;
    /** Marked done by staff without sharing it: when (the work day, noon MYT, or now) and who. Absent or null: not done. */
    done?: { at: Stamp; by: string } | null;
    /** null = a file whose comments Oura does not read (PDF, Sheet). */
    threads: MockThread[] | null;
    /** The last read of its comments failed. */
    commentsError: boolean;
    /** Phase 2: the numbered question list Oura last wrote into the body (B15). */
    bodyQuestionList?: string[] | null;
}
interface MockDeliverable { code: string; label: string; months: number[] }
/**
 * The signed engagement, as far as documents need it. `startedAt` is the
 * contract start (`engagement.started_at`); Day 1 of the timeline is the
 * Monday after it, and each code's doc is due in the window of its first
 * month in `deliverables[].months` (G1 each month).
 */
interface MockContract { startedAt: Stamp; termMonths: number; outlets: number; deliverables: MockDeliverable[] }
interface MockFolder {
    folderId: string;
    url: string;
    name: string;
    linkedAt: Stamp;
    linkedBy: string;
    lastReadAt: Stamp | null;
    /** False: oura@ cannot open it, so every read fails. */
    readable: boolean;
    lastError: { code: string; message: string; at: Stamp } | null;
    /** A read asked for ("Read now", or just linked), finishing at this ISO time. */
    pendingReadAt: string | null;
}
export interface MockBusiness { id: string; name: string; contract: MockContract | null; folder: MockFolder | null }
/**
 * `rev` is the fixture revision a saved store was seeded or brought up to.
 * Rev 2 (29 Sep 2026) writes the fixture names' codes with commas.
 */
interface DocsStore {
    version: 1;
    rev?: number;
    businesses: MockBusiness[];
    files: MockFile[];
    seq: number;
    /** The seed extensions applied to this store, by name and revision ("docs-write": 1). */
    extensions?: Record<string, number>;
}

const STORE_KEY = "__oura_docs_store_v1__";
const FIXTURE_REV = 3;

/**
 * Rev 2: the decided code format is "(S1, S3, B1, G4)". A saved store keeps
 * its state (shares, picks, replies) and only these fixture names move, and
 * only while they still read as seeded. Riverside's Answer Plan keeps the
 * older "(S1 S3 B1 G4)" and F4 its legacy "W2 (S4)" name, so old names are
 * seen to read.
 */
const REV2_NAMES: Record<string, { from: string; name: string; pageTitle?: [string, string] }> = {
    doc_f1_answer_plan: {
        from: "ourteam_Nyonya Table_Answer Plan (S1 S3 B1 G4)_v1.0_{d}",
        name: "ourteam_Nyonya Table_Answer Plan (S1, S3, B1, G4)_v1.0_{d}",
        pageTitle: ["Answer Plan (S1 S3 B1 G4)\nNyonya Table · v1.0", "Answer Plan (S1, S3, B1, G4)\nNyonya Table · v1.0"],
    },
    doc_f11_trashed: {
        from: "Copy of ourteam_Nyonya Table_Answer Plan (S1 S3 B1 G4)_v1.0_{d}",
        name: "Copy of ourteam_Nyonya Table_Answer Plan (S1, S3, B1, G4)_v1.0_{d}",
    },
    doc_cf_answer_plan: {
        from: "ourteam_Crawl-Failed Business_Answer Plan (S1 S3 B1 G4)_v1.0_{d}",
        name: "ourteam_Crawl-Failed Business_Answer Plan (S1, S3, B1, G4)_v1.0_{d}",
        pageTitle: ["Answer Plan (S1 S3 B1 G4)", "Answer Plan (S1, S3, B1, G4)"],
    },
};

/**
 * Rev 3 (6 Oct 2026): the name Oura writes is
 * `(<codes>) <Client>_<Doc>_<Location>_<DD MMMM YYYY>_<ver>`, so the fixtures Oura
 * wrote move to it. The ones the TEAM wrote stay as they are — Riverside's
 * house name with space-separated codes, F4's legacy "W2 (S4) …", the
 * crawl-failed business's "Business Details (S4)" and the trashed Drive copy —
 * so the board is still seen reading every older name it holds. Only the file
 * name moves: codes, versions, dates and page titles are untouched.
 */
const REV3_NAMES: Record<string, { from: string; name: string }> = {
    doc_f1_answer_plan: { from: "ourteam_Nyonya Table_Answer Plan (S1, S3, B1, G4)_v1.0_{d}", name: "(S1-S3-B1-G4) Nyonya Table_Answer Plan_{S}_1.0" },
    doc_f2_gbp_mont_kiara: { from: "ourteam_Nyonya Table_GBP Pack Mont Kiara (S2-MK)_v1.0_{d}", name: "(S2) Nyonya Table_GBP Pack_Mont Kiara_{S}_1.0" },
    doc_f3_gbp_bukit_bintang: { from: "ourteam_Nyonya Table_GBP Pack Bukit Bintang (S2-BB)_v1.0_{d}", name: "(S2) Nyonya Table_GBP Pack_Bukit Bintang_{S}_1.0" },
    doc_f6_copy_pack: { from: "ourteam_Nyonya Table_Copy Pack (B1)_v1.0_{d}", name: "(B1) Nyonya Table_Copy Pack_{S}_1.0" },
    doc_f7_copy_pack_home: { from: "ourteam_Nyonya Table_Copy Pack Home (B1)_v1.0_{d}", name: "(B1) Nyonya Table_Copy Pack Home_{S}_1.0" },
    doc_f8_competitor_watch: { from: "ourteam_Nyonya Table_Competitor Watch (G4)_v1.0_{d}", name: "(G4) Nyonya Table_Competitor Watch_{S}_1.0" },
    doc_f9_loyalty_card: { from: "ourteam_Nyonya Table_Loyalty Card Page (X2)_v1.0_{d}", name: "(X2) Nyonya Table_Loyalty Card Page_{S}_1.0" },
    doc_f13_page_titles: { from: "ourteam_Nyonya Table_Page Titles Review (S1)_v1.0_{d}", name: "(S1) Nyonya Table_Page Titles Review_{S}_1.0" },
};

/** Bring a saved store up to the current fixture revision, keeping its state. */
function migrateStore(saved: DocsStore): DocsStore {
    const was = saved.rev ?? 1;
    if (was >= FIXTURE_REV) return saved;
    for (const f of saved.files) {
        if (was < 2) {
            const m = REV2_NAMES[f.id];
            if (m) {
                if (f.name === m.from) f.name = m.name;
                if (m.pageTitle && f.pageTitle === m.pageTitle[0]) f.pageTitle = m.pageTitle[1];
            }
        }
        const m3 = REV3_NAMES[f.id];
        // Rev 2's name is the one rev 3 moves from, so a rev-1 store renamed
        // just above is carried the rest of the way in the same pass.
        if (m3 && f.name === m3.from) f.name = m3.name;
    }
    saved.rev = FIXTURE_REV;
    return saved;
}
/** The staffer the sample store acts as. The server takes it from the token. */
const MOCK_STAFF = "Angel";

/** Assigned at the bottom of the module, once the fixtures below are defined. */
let store: DocsStore;

function loadStore(): DocsStore {
    try {
        const raw = window.localStorage.getItem(STORE_KEY);
        if (raw) {
            const parsed = JSON.parse(raw) as DocsStore;
            if (parsed && parsed.version === 1 && Array.isArray(parsed.files) && Array.isArray(parsed.businesses)) return applyExtensions(migrateStore(parsed));
        }
    } catch {
        /* private mode, or a stale shape: reseed */
    }
    return applyExtensions(seedStore());
}

function saveStore(): void {
    try {
        window.localStorage.setItem(STORE_KEY, JSON.stringify(store));
    } catch {
        /* ignore */
    }
}

/** Reseed the fixtures. Exposed as `window.__ouraResetDocs()`. */
export function resetDocsStore(): void {
    store = applyExtensions(seedStore());
    saveStore();
    changed();
}

/* ------------------------------------------------------------------------- *
 * Seed extensions: another sample store adding its fixtures to this one
 * ------------------------------------------------------------------------- */

interface SeedExtension { name: string; rev: number; apply: (files: MockFile[], businesses: MockBusiness[], nextSeq: () => number) => void }
const extensions: SeedExtension[] = [];

/** Apply every registered extension a store has not had at its revision. */
function applyExtensions(target: DocsStore): DocsStore {
    for (const ext of extensions) {
        const have = target.extensions?.[ext.name] ?? 0;
        if (have >= ext.rev) continue;
        ext.apply(target.files, target.businesses, () => ++target.seq);
        target.extensions = { ...(target.extensions ?? {}), [ext.name]: ext.rev };
    }
    return target;
}

/**
 * Add fixtures after every seed (and once to the store as it stands): the
 * write sample's Drive-shaped data, so Phase 1 reads it. Applied again only
 * when `rev` moves past the revision the store has, or after a reseed.
 */
export function registerDocsSeedExtension(name: string, rev: number, apply: SeedExtension["apply"]): void {
    if (!extensions.some((e) => e.name === name)) extensions.push({ name, rev, apply });
    if (!store) return;
    const before = store.extensions?.[name] ?? 0;
    applyExtensions(store);
    if ((store.extensions?.[name] ?? 0) !== before) {
        saveStore();
        changed();
    }
}

const nextId = (p: string) => `${p}_${++store.seq}`;

/* ------------------------------------------------------------------------- *
 * Time
 * ------------------------------------------------------------------------- */

interface Ctx { now: number; nowIso: string; today: string; sync: string }

function ctxNow(): Ctx {
    const now = Date.now();
    const sync = new Date(Math.floor(now / 300_000) * 300_000).toISOString();
    return { now, nowIso: new Date(now).toISOString(), today: myDate(new Date(now)), sync };
}

const STAMP_RE = /^@(wd|min|h|cal|sync)(?::(-?\d+))?(?:T(\d{2}):(\d{2}))?$/;

/** A stamp as an ISO instant. */
function at(stamp: Stamp, ctx: Ctx): string {
    const m = STAMP_RE.exec(stamp);
    if (!m) return stamp;
    const n = Number(m[2] ?? 0);
    switch (m[1]) {
        case "sync":
            return ctx.sync;
        case "min":
            return new Date(ctx.now + n * 60_000).toISOString();
        case "h":
            return new Date(ctx.now + n * 3_600_000).toISOString();
        case "cal":
            return myInstant(addDays(ctx.today, n), Number(m[3] ?? 10), Number(m[4] ?? 0));
        default: {
            // shiftWorkingDays counts BACK for a positive n, so "@wd:-8" (n = -8) is 8 working days ago.
            const day = shiftWorkingDays(ctx.today, -n);
            return myInstant(day, Number(m[3] ?? 10), Number(m[4] ?? 0));
        }
    }
}
const atOrNull = (s: Stamp | null, ctx: Ctx) => (s ? at(s, ctx) : null);
/** A stamp as a Malaysian date. */
const dateOf = (s: Stamp, ctx: Ctx) => myDate(at(s, ctx));

const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTH_LONG = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
function nameDate(ymd: string, long: boolean): string {
    const [y, m, d] = ymd.split("-").map(Number);
    return `${d} ${(long ? MONTH_LONG : MONTH_SHORT)[m - 1]} ${y}`;
}

/** The file name as it stands, with its date filled in. */
function fileName(f: MockFile, ctx: Ctx): string {
    if (!f.nameDate) return f.name;
    const ymd = dateOf(f.nameDate, ctx);
    let name = f.name.replace("{d}", nameDate(ymd, false)).replace("{D}", nameDate(ymd, true)).replace("{S}", docDateOf(ymd));
    // The long-name fixture keeps exactly 140 characters whatever the date's length.
    if (name.includes("{fit140:")) {
        name = name.replace(/\{fit140:([^}]*)\}/, (_all, phrase: string) => {
            const without = name.replace(/\{fit140:[^}]*\}/, "").length;
            return phrase.slice(0, Math.max(0, 140 - without));
        });
    }
    return name;
}

/* ------------------------------------------------------------------------- *
 * Computing a document (the A.4 rules)
 * ------------------------------------------------------------------------- */

interface Spoken { id: string; threadId: string; author: DocAuthor; text: string; at: string; action: DocPost["action"]; where: DocPost["where"] }

interface Computed {
    detail: DocumentDetail;
    /** Effective codes for the board, with the outlet on its code. */
    chips: Array<{ code: string; outlet: string | null }>;
    sharedAtIso: string | null;
    title: string;
}

function authorOf(a: MockAuthor, onBehalfOf: MockAuthor | null = null): DocAuthor {
    const w = whoSpokeFor(a.email, onBehalfOf?.name ?? null);
    return w.onBehalfOf ? { name: w.onBehalfOf, side: w.side, account: w.account, onBehalfOf: w.onBehalfOf } : { name: a.name, side: w.side, account: w.account };
}

/** A stored reply as the contract's post (B26 fields only when Oura wrote it). */
function postOf(r: MockReply, ctx: Ctx): DocPost {
    const p: DocPost = { id: r.id, author: authorOf(r.by, r.onBehalfOf ?? null), text: r.text, at: at(r.at, ctx), action: r.action, where: r.where ?? "doc" };
    if (r.via) p.via = r.via;
    if (r.answer) p.answer = r.answer;
    if (r.loggedBy) p.loggedBy = r.loggedBy;
    if (r.saidAt) p.saidAt = r.saidAt;
    if (r.signedBy) p.signedBy = r.signedBy;
    if (r.editedAt) p.editedAt = r.editedAt;
    if (r.file) p.file = r.file;
    return p;
}

function contractCodes(b: MockBusiness): Set<string> | null {
    return b.contract ? new Set(b.contract.deliverables.map((d) => d.code)) : null;
}

function plural(n: number, one: string, many = `${one}s`): string {
    return `${n} ${n === 1 ? one : many}`;
}

function trimTo(s: string, max: number): string {
    const t = s.replace(/\s+/g, " ").trim();
    return t.length <= max ? t : `${t.slice(0, max - 1).trimEnd()}…`;
}

/** Page title, else docType + codes, else the file name. */
function titleOf(pageTitle: string | null, docType: string | null, codes: string[], outlet: string | null, name: string): string {
    const line1 = pageTitle?.split("\n")[0]?.trim();
    if (line1) return line1;
    if (docType) {
        const labels = codeChipsOf(codes, outlet).map((c) => c.label);
        return labels.length ? `${docType} (${joinCodes(labels)})` : docType;
    }
    return name;
}

function computeDoc(f: MockFile, b: MockBusiness, readAt: string | null, ctx: Ctx): Computed {
    const name = fileName(f, ctx);
    const fileType = fileTypeOf(f.mimeType);
    const parsed = parseDocName(name);
    const titleCodes = parseTitleCodes(f.pageTitle);

    // Rule 11: the name first; a staff pick only while the name carries none; the title as backup.
    let codes: string[] = [];
    let outlet: string | null = null;
    let codeSource: CodeSource | null = null;
    if (parsed.codes.length) {
        codes = parsed.codes;
        // A client doc's name no longer writes the outlet on a code: it carries
        // the place in its own Location segment ("…_GBP Pack_Mont Kiara_…").
        // Line 1 still writes "(S2-MK)", so the outlet comes from there when
        // the name gives none — which is what the server's own record holds.
        outlet = parsed.outlet ?? (titleCodes && titleCodes.codes.some((c) => parsed.codes.includes(c)) ? titleCodes.outlet : null);
        codeSource = "name";
    } else if (f.pick && f.pick.codes.length) {
        codes = f.pick.codes;
        outlet = f.pick.outlet;
        codeSource = "staff";
    } else if (titleCodes) {
        codes = titleCodes.codes;
        outlet = titleCodes.outlet;
        codeSource = "title";
    }
    if (f.notClientDoc) {
        codes = [];
        outlet = null;
        codeSource = null;
    }

    // Rule 6: sharing.
    const sharedWith: DocSharedWith[] = f.permissions
        .filter((p) => p.type !== "anyone")
        .map((p) => ({ email: p.email, name: p.name, role: p.role, ours: isOurEmail(p.email) }));
    const clientPerms = f.permissions.filter((p) => (p.type === "user" || p.type === "group") && p.email && !isOurEmail(p.email) && p.role !== "owner");
    const clientCanSee = clientPerms.length > 0;
    const anyone = f.permissions.find((p) => p.type === "anyone");
    const linkSharing: LinkRole | null = anyone && anyone.role !== "owner" ? anyone.role : null;
    const sharedAtIso = clientCanSee ? atOrNull(f.sharedAt, ctx) : null;

    // Rule 8: reply-by.
    const replyBy = f.replyBy ?? (sharedAtIso ? defaultReplyBy(sharedAtIso) : null);
    const replyBySource = f.replyBy ? "staff" as const : sharedAtIso ? "default" as const : null;
    const lateAllowed = clientCanSee && replyBy !== null;
    const pastReplyBy = lateAllowed && replyBy !== null && ctx.today > replyBy;
    const lateDays = pastReplyBy && replyBy ? workingDaysBetween(replyBy, ctx.today) : null;

    // Rules 1 to 3: threads.
    const threads: DocThread[] = [];
    const spokenAll: Spoken[] = [];
    const lastSpokenOf = new Map<string, Spoken>();
    for (const t of f.threads ?? []) {
        const openedBy = authorOf(t.by);
        const openedAt = at(t.at, ctx);
        // B26: a comment Oura posted as a question (its id is on record) is a question, whatever its words.
        const kind = t.question ? "question" : threadKind(openedBy.side, t.text);
        const posts: DocPost[] = t.replies.map((r) => postOf(r, ctx)).sort((x, y) => Date.parse(x.at) - Date.parse(y.at));
        let resolved = false;
        let resolvedBy: DocAuthor | null = null;
        let resolvedAt: string | null = null;
        let resolvedAgreedNow = false;
        // The client's last words, as the agreement rule reads them (B26).
        let lastClientWord: ClientWord | null = openedBy.side === "client" ? { text: t.text } : null;
        for (const p of posts) {
            if (p.action === "resolve") {
                resolved = true;
                resolvedBy = p.author;
                resolvedAt = p.at;
                resolvedAgreedNow = resolveCarriesAgreement(p.author.side, lastClientWord, t.question?.kind ?? null);
            } else if (p.action === "reopen") {
                resolved = false;
                resolvedBy = null;
                resolvedAt = null;
                resolvedAgreedNow = false;
            }
            if (p.author.side === "client" && p.action === "reply" && p.text.trim()) lastClientWord = { text: p.text, answer: p.answer ?? null };
        }
        const spoken: Spoken[] = [
            { id: `${t.id}:open`, threadId: t.id, author: openedBy, text: t.text, at: openedAt, action: "reply", where: "doc" },
            ...posts.filter((p) => p.text.trim()).map((p) => ({ id: p.id, threadId: t.id, author: p.author, text: p.text, at: p.at, action: p.action, where: p.where })),
        ];
        const last = spoken[spoken.length - 1];
        spokenAll.push(...spoken);
        lastSpokenOf.set(t.id, last);

        let state: ThreadState | null = null;
        if (kind === "question") {
            if (resolved) state = "approved";
            else if (last.author.side === "client") state = "needs_us";
            else state = pastReplyBy ? "late" : "waiting_on_client";
        } else if (!resolved && last.author.side === "client") {
            state = "needs_us";
        }
        const q = t.question ?? null;
        threads.push({
            id: t.id,
            driveCommentId: t.id.replace(/^thr_/, "AAAB"),
            kind,
            code: q?.code ?? threadCodePrefix(t.text),
            state,
            internal: kind === "our_note" && openedBy.account === "staff",
            quoted: t.quoted,
            openedBy,
            openedAt,
            text: t.text,
            posts,
            resolved,
            resolvedBy,
            resolvedAt,
            lateWorkingDays: state === "late" ? lateDays : null,
            questionId: q?.id ?? null,
            questionKind: q?.kind ?? t.askKind ?? null,
            number: q?.number ?? null,
            options: q?.options ?? [],
            resolvedAgreed: kind === "question" && resolved ? resolvedAgreedNow : null,
        });
    }

    const read = f.threads !== null;
    const questions = threads.filter((t) => t.kind === "question");
    const clientTouched = (t: DocThread) => t.openedBy.side === "client" || t.posts.some((p) => p.author.side === "client");
    const q = read
        ? {
            out: questions.length,
            back: questions.filter(clientTouched).length,
            waitingOnClient: questions.filter((t) => t.state === "waiting_on_client" || t.state === "late").length,
            needsUs: questions.filter((t) => t.state === "needs_us").length,
            approved: questions.filter((t) => t.state === "approved").length,
            late: questions.filter((t) => t.state === "late").length,
        }
        : null;
    const raised = threads.filter((t) => t.kind === "raised_by_client");
    const openInternal = threads.filter((t) => t.internal && !t.resolved);

    // Rule 4: the doc's state, first match wins.
    // A doc staff marked done reads done whatever the threads say (backend #6364).
    const doneAt = f.done ? at(f.done.at, ctx) : null;
    const state: DocState | null = doneAt ? "done" : docStateOf(clientCanSee, read, threads);
    // B26: questions we resolved without the client's agreement.
    const unagreed = questions.filter((t) => t.resolvedAgreed === false);

    const late = !read || !lateAllowed ? null : questions.some((t) => t.state === "late");

    // Rule 9: waiting.
    let waitingOn: StaffDocument["waitingOn"] = null;
    let waitingSince: string | null = null;
    if (state === "needs_us") {
        const lasts = threads.filter((t) => t.state === "needs_us").map((t) => lastSpokenOf.get(t.id)).filter((s): s is Spoken => !!s);
        waitingSince = lasts.map((s) => s.at).sort()[0] ?? null;
        waitingOn = "us";
    } else if (state === "waiting_on_client" || state === "late") {
        const lasts = questions.filter((t) => t.state === "waiting_on_client" || t.state === "late").map((t) => lastSpokenOf.get(t.id)).filter((s): s is Spoken => !!s);
        const earliest = lasts.map((s) => s.at).sort()[0] ?? null;
        waitingSince = earliest && sharedAtIso && earliest < sharedAtIso ? sharedAtIso : earliest;
        waitingOn = "client";
    }
    const waitingWorkingDays = waitingSince ? workingDaysBetween(myDate(waitingSince), ctx.today) : null;

    // Last posts and rule 10: took.
    const byTime = [...spokenAll].sort((x, y) => Date.parse(x.at) - Date.parse(y.at));
    const toSummary = (s: Spoken): DocPostSummary => ({ id: s.id, author: s.author, text: s.text, at: s.at, action: s.action, where: s.where, threadId: s.threadId });
    const lastPost = byTime.length ? toSummary(byTime[byTime.length - 1]) : null;
    const clientPosts = byTime.filter((s) => s.author.side === "client");
    const lastClientPost = clientPosts.length ? toSummary(clientPosts[clientPosts.length - 1]) : null;
    const firstClientReplyAt = clientPosts[0]?.at ?? null;
    const firstQuestionAt = questions.map((t) => t.openedAt).sort()[0] ?? null;
    const startIso = [sharedAtIso, firstQuestionAt].filter((x): x is string => !!x).sort().pop() ?? null;
    const tookWorkingDays = firstClientReplyAt && startIso ? workingDaysBetween(myDate(startIso), myDate(firstClientReplyAt)) : null;

    // Flags.
    const flags: DocFlag[] = [];
    if (linkSharing) {
        const verb = linkSharing === "writer" ? "edit" : linkSharing === "commenter" ? "comment on" : "open";
        flags.push({ code: "link_sharing", tone: "red", message: `Anyone with the link can ${verb} it. Share it to the client's email, comment-only, and turn the link off.`, detail: null });
    }
    const writers = clientPerms.filter((p) => p.role === "writer");
    if (writers.length) {
        flags.push({ code: "edit_access", tone: "red", message: "The client can edit it. The rule is comment-only.", detail: writers.map((p) => `${p.email ?? p.name ?? "Someone"}, editor`) });
    }
    // Exposed: shared to the client, or open to anyone with the link (the 23 Sep incident).
    if ((clientCanSee || linkSharing) && openInternal.length) {
        flags.push({ code: "internal_notes", tone: "red", message: `${plural(openInternal.length, "internal note")} the client can read`, detail: openInternal.map((t) => trimTo(t.text, 120)) });
    }
    if (!f.notClientDoc && codes.length === 0) {
        // Say why when the name has a code-like group Oura could not read, such as "(S9)" or "(S1,S3)".
        const unread = parsed.codes.length === 0 ? unreadCodeGroup(name) : null;
        // 2 outlets in 1 name: each outlet gets its own doc, so neither is filed from it.
        const outlets = unread ? outletsInGroup(unread) : [];
        const message = outlets.length > 1
            ? `The name has ${unread}: ${outlets.length} outlets in 1 doc. Each outlet needs its own doc. Pick one.`
            : unread
                ? `The name has ${unread}, which isn't a code Oura can read. Pick one.`
                : "No code in the name";
        flags.push({ code: "no_code", tone: "amber", message, detail: null });
    }
    if (parsed.codes.length && titleCodes) {
        const a = codeChipsOf(parsed.codes, parsed.outlet).map((c) => c.label);
        const t = codeChipsOf(titleCodes.codes, titleCodes.outlet).map((c) => c.label);
        if ([...a].sort().join(" ") !== [...t].sort().join(" ")) {
            flags.push({ code: "name_title_disagree", tone: "amber", message: `The name says ${joinCodes(a)}. The page title says ${joinCodes(t)}.`, detail: null });
        }
    }
    const contract = contractCodes(b);
    const outside = contract ? codes.filter((c) => !contract.has(c)) : [];
    if (outside.length) {
        flags.push({ code: "code_not_in_contract", tone: "amber", message: `${joinCodes(outside)} ${outside.length === 1 ? "is" : "are"} not in this client's contract.`, detail: null });
    }
    if (fileType === "word") flags.push({ code: "word_file", tone: "amber", message: "A Word file, not a Google Doc", detail: null });
    if (f.commentsError) flags.push({ code: "comments_unreadable", tone: "amber", message: "Oura could not read its comments on the last run.", detail: null });
    if (unagreed.length) {
        flags.push({
            code: "resolved_without_agreement",
            tone: "amber",
            message: `${plural(unagreed.length, "question")} resolved without the client's agreement`,
            detail: unagreed.map((t) => trimTo(t.text, 120)),
        });
    }

    const detail: DocumentDetail = {
        id: f.id,
        driveFileId: f.driveFileId,
        name,
        pageTitle: f.pageTitle,
        fileType,
        url: fileUrl(f),
        docType: parsed.docType,
        codes,
        outlet,
        codeSource,
        version: parsed.version,
        docDate: parsed.date,
        notClientDoc: f.notClientDoc,
        owner: f.owner,
        modifiedAt: atOrNull(f.modifiedAt, ctx),
        sharing: { clientCanSee, sharedWith, linkSharing, sharedAt: sharedAtIso },
        replyBy,
        replyBySource,
        state,
        done: doneAt ? { at: doneAt, by: f.done?.by ?? MOCK_STAFF } : null,
        late,
        lateWorkingDays: late ? lateDays : null,
        waitingOn,
        waitingSince,
        waitingWorkingDays,
        questions: q,
        raisedByClient: read ? { total: raised.length, needsUs: raised.filter((t) => t.state === "needs_us").length } : null,
        notCounted: read
            ? {
                ourNotes: threads.filter((t) => t.kind === "our_note" && !t.internal).length,
                placeholders: threads.filter((t) => t.kind === "placeholder").length,
                internalNotes: threads.filter((t) => t.internal).length,
            }
            : null,
        lastPost,
        lastClientPost,
        firstClientReplyAt,
        tookWorkingDays,
        flags,
        readAt,
        unagreedResolves: read ? unagreed.length : null,
        threads,
    };
    return {
        detail,
        chips: codeChipsOf(codes, outlet).map((c) => ({ code: c.code, outlet: c.outlet })),
        sharedAtIso,
        title: titleOf(f.pageTitle, parsed.docType, codes, outlet, name),
    };
}

function fileUrl(f: MockFile): string {
    const t = fileTypeOf(f.mimeType);
    if (t === "gdoc" || t === "word") return `https://docs.google.com/document/d/${f.driveFileId}/edit`;
    if (t === "sheet") return `https://docs.google.com/spreadsheets/d/${f.driveFileId}/edit`;
    return `https://drive.google.com/file/d/${f.driveFileId}/view`;
}

function staffDoc(d: DocumentDetail): StaffDocument {
    const { threads: _threads, ...rest } = d;
    void _threads;
    return rest;
}

/* ------------------------------------------------------------------------- *
 * Folders, the board and codes
 * ------------------------------------------------------------------------- */

/** Finish a read that was asked for, once its time has come. */
function settleReads(ctx: Ctx): boolean {
    let touched = false;
    for (const b of store.businesses) {
        const folder = b.folder;
        if (!folder?.pendingReadAt || folder.pendingReadAt > ctx.nowIso) continue;
        if (folder.readable) {
            folder.lastReadAt = folder.pendingReadAt;
            folder.lastError = null;
        } else {
            folder.lastError = { code: "folder_not_readable", message: DOCS_ERROR_MESSAGE.folder_not_readable, at: folder.pendingReadAt };
        }
        folder.pendingReadAt = null;
        touched = true;
    }
    return touched;
}

/** When Oura last read this folder: the 5-minute clock for a healthy folder, the last good read for a failing one. */
function folderReadAt(folder: MockFolder, ctx: Ctx): string | null {
    const last = atOrNull(folder.lastReadAt, ctx);
    if (!last) return null;
    if (!folder.readable || folder.lastError) return last;
    return last > ctx.sync ? last : ctx.sync;
}

function folderFiles(b: MockBusiness): MockFile[] {
    const folder = b.folder;
    if (!folder) return [];
    return store.files.filter((f) => f.businessId === b.id && f.folderId === folder.folderId && !f.trashed);
}

function toFolder(b: MockBusiness, ctx: Ctx): DocsFolder | null {
    const folder = b.folder;
    if (!folder) return null;
    const readAt = folderReadAt(folder, ctx);
    return {
        businessId: b.id,
        folderId: folder.folderId,
        url: folder.url,
        name: readAt ? folder.name : null,
        linkedAt: at(folder.linkedAt, ctx),
        linkedBy: folder.linkedBy,
        lastReadAt: readAt,
        lastError: folder.lastError ? { code: folder.lastError.code, message: folder.lastError.message, at: at(folder.lastError.at, ctx) } : null,
        fileCount: readAt ? folderFiles(b).length : null,
    };
}

function connectionOf(b: MockBusiness, ctx: Ctx): DocsConnection["status"] {
    if (!b.folder) return "not_connected";
    if (b.folder.lastError) return "action_needed";
    void ctx;
    return "connected";
}

interface Board { payload: BusinessDocuments; computed: Computed[] }

const FAMILY_ORDER = ["S", "A", "B", "G", "X"];
function codeSortKey(code: string, outlet: string | null): string {
    const fam = FAMILY_ORDER.indexOf(code[0]);
    const num = Number(code.slice(1)) || 0;
    return `${fam < 0 ? 9 : fam}-${String(num).padStart(3, "0")}-${outlet ?? ""}`;
}

const CODE_STATE_ORDER: DocState[] = ["needs_us", "late", "waiting_on_client", "approved", "nothing_asked"];

function computeBoard(b: MockBusiness, ctx: Ctx): Board {
    const folder = toFolder(b, ctx);
    const connection = connectionOf(b, ctx);
    const readAt = folder?.lastReadAt ?? null;
    if (!folder || !readAt) {
        return { payload: { folder, connection, readAt, documents: [], codes: [], counts: null }, computed: [] };
    }
    const computed = folderFiles(b).map((f) => computeDoc(f, b, readAt, ctx));
    const docs = computed.map((c) => c.detail);

    // Rows: every contract code, the outlets seen on docs, and any code on a doc that is not in the contract.
    const contract = b.contract;
    // Day 1 of Oura's timeline: the Monday after the contract's start (build_schedule's clock_start).
    const day1 = contract ? planDay1(dateOf(contract.startedAt, ctx)) : null;
    const inContract = contractCodes(b) ?? new Set<string>();
    const rowKeys = new Map<string, { code: string; outlet: string | null }>();
    const key = (code: string, outlet: string | null) => `${code}|${outlet ?? ""}`;
    const covering = new Map<string, Computed[]>();
    for (const c of computed) {
        if (c.detail.notClientDoc) continue;
        for (const chip of c.chips) {
            const k = key(chip.code, chip.outlet);
            rowKeys.set(k, chip);
            covering.set(k, [...(covering.get(k) ?? []), c]);
        }
    }
    Array.from(inContract).forEach((code) => {
        const hasOutletRows = Array.from(rowKeys.values()).some((r) => r.code === code && r.outlet);
        if (!hasOutletRows) rowKeys.set(key(code, null), { code, outlet: null });
    });

    const rows: CodeRow[] = Array.from(rowKeys.values()).map(({ code, outlet }) => {
        const docsHere = covering.get(key(code, outlet)) ?? [];
        const shared = docsHere.filter((c) => c.detail.sharing.clientCanSee);
        // B5: the code's plan month from the contract, that month's window from the timeline.
        const months = contract?.deliverables.find((d) => d.code === code)?.months ?? [];
        const sharedOn = shared.map((c) => (c.sharedAtIso ? myDate(c.sharedAtIso) : null));
        const exp = contract && inContract.has(code) ? codeExpectation(code, months, day1, ctx.today, sharedOn) : null;
        let state: CodeState;
        if (exp?.notSentYet) state = "not_sent_yet";
        else if (shared.length === 0) state = docsHere.length ? "not_shared" : "not_due";
        else {
            // B26: each shared doc's state for this code, from the questions that cover it.
            const states = shared
                .map((c) => docStateForCode(c.detail.sharing.clientCanSee, c.detail.questions !== null, c.detail.threads, code, outlet))
                .filter((s): s is DocState => !!s);
            state = (CODE_STATE_ORDER.find((s) => states.includes(s)) as CodeState | undefined) ?? "nothing_asked";
        }
        const late = state === "not_sent_yet"
            ? exp?.late ?? null
            : shared.some((c) => c.detail.late === true)
                ? true
                : shared.some((c) => c.detail.late === false) || exp
                    ? false
                    : null;
        // S2 on its bare row wants 1 pack per outlet; G1 1 per plan month started.
        const expectedDocs = exp ? (code === "S2" && !outlet ? contract?.outlets ?? 1 : exp.docs) : null;
        return {
            code,
            outlet,
            label: contract?.deliverables.find((d) => d.code === code)?.label ?? null,
            inContract: inContract.has(code),
            state,
            documentIds: docsHere.map((c) => c.detail.id),
            expectedBy: exp?.by ?? null,
            expectedMonth: exp?.month ?? null,
            expectedDocs,
            sharedDocs: exp ? exp.shared : shared.length,
            late,
        };
    });
    rows.sort((x, y) => codeSortKey(x.code, x.outlet).localeCompare(codeSortKey(y.code, y.outlet)));

    const counted = docs.filter((d) => !d.notClientDoc && d.codes.length > 0);
    const counts = {
        waitingOnClient: counted.filter((d) => d.state === "waiting_on_client" || d.state === "late").length,
        needsUs: counted.filter((d) => d.state === "needs_us").length,
        late: counted.filter((d) => d.late === true).length,
        notSentYet: rows.filter((r) => r.state === "not_sent_yet").length,
        approved: counted.filter((d) => d.state === "approved").length,
        noCode: docs.filter((d) => !d.notClientDoc && d.codes.length === 0).length,
        redFlags: docs.filter((d) => d.flags.some((f) => f.tone === "red")).length,
    };
    return { payload: { folder, connection, readAt, documents: docs.map(staffDoc), codes: rows, counts }, computed };
}

/* ------------------------------------------------------------------------- *
 * Merchant documents
 * ------------------------------------------------------------------------- */

const MERCHANT_ORDER: MerchantDocState[] = ["needs_your_reply", "with_us", "nothing_asked", "approved"];

function merchantDocs(b: MockBusiness, ctx: Ctx): MerchantDocument[] | undefined {
    // Absent until the folder's first read: Oura doesn't read docs here yet.
    if (!b.folder || !folderReadAt(b.folder, ctx)) return undefined;
    const board = computeBoard(b, ctx);
    const out: MerchantDocument[] = [];
    for (const c of board.computed) {
        const d = c.detail;
        if (!d.sharing.clientCanSee || d.notClientDoc) continue;
        // A Google Doc or Word file shows once its comments are read, so the
        // merchant never sees a guessed "Nothing to answer". A PDF or Sheet,
        // whose comments Oura doesn't read, shows once shared, with no count.
        if ((d.fileType === "gdoc" || d.fileType === "word") && d.questions === null) continue;
        const questions = d.threads.filter((t) => t.kind === "question");
        const waiting = questions.filter((t) => t.state === "waiting_on_client" || t.state === "late");
        const read = d.questions !== null;
        let state: MerchantDocState;
        if (waiting.length > 0) state = "needs_your_reply";
        else if (d.state === "needs_us") state = "with_us";
        else if (d.state === "approved") state = "approved";
        else state = "nothing_asked";
        out.push({
            id: d.id,
            title: c.title,
            codes: d.codes,
            outlet: d.outlet,
            fileType: d.fileType,
            url: d.url,
            sharedAt: d.sharing.sharedAt,
            replyBy: d.replyBy,
            late: d.late,
            lateWorkingDays: d.lateWorkingDays,
            state,
            questions: read ? { out: questions.length, waiting: waiting.length, approved: questions.filter((t) => t.state === "approved").length } : null,
            openQuestions: waiting.map((t) => {
                const rec = store.files.find((x) => x.id === d.id)?.threads?.find((x) => x.id === t.id)?.question ?? null;
                return {
                    id: t.id,
                    text: rec ? rec.text : t.text.replace(/^\s*\[[^\]]*\]\s*/, ""),
                    askedAt: t.openedAt,
                    code: t.code,
                    questionId: t.questionId ?? null,
                    kind: t.questionKind ?? null,
                    number: t.number ?? null,
                    options: t.options ?? [],
                };
            }),
            updatedAt: d.lastPost?.at ?? d.modifiedAt,
        });
    }
    return out.sort((x, y) => {
        const s = MERCHANT_ORDER.indexOf(x.state) - MERCHANT_ORDER.indexOf(y.state);
        if (s) return s;
        return (x.replyBy ?? "9999").localeCompare(y.replyBy ?? "9999");
    });
}

/**
 * `ClientPagePayload.documents` for the sample merchant page: undefined when
 * no folder is linked (Oura doesn't read docs there yet), [] when nothing is
 * shared with them.
 */
export function mockMerchantDocuments(businessId: string): MerchantDocument[] | undefined {
    const ctx = ctxNow();
    if (settleReads(ctx)) saveStore();
    const b = store.businesses.find((x) => x.id === businessId);
    const list = b ? merchantDocs(b, ctx) : undefined;
    return list && decorators.merchant ? decorators.merchant(list, b!.id) : list;
}

/* ------------------------------------------------------------------------- *
 * All clients
 * ------------------------------------------------------------------------- */

function computeAcross(ctx: Ctx): DocsAcrossClients {
    const clients: AcrossClient[] = [];
    const waitingOnClient: AcrossRow[] = [];
    const needsUs: AcrossRow[] = [];
    const late: AcrossRow[] = [];
    const since180 = addDays(ctx.today, -180);
    for (const b of store.businesses) {
        const board = computeBoard(b, ctx);
        const counts = board.payload.counts;
        const counted = board.computed.filter((c) => !c.detail.notClientDoc && c.detail.codes.length > 0);
        const sent = counted.filter((c) => c.sharedAtIso && (c.detail.questions?.out ?? 0) > 0);
        const answered = sent.filter((c) => c.detail.tookWorkingDays !== null && c.detail.firstClientReplyAt && myDate(c.detail.firstClientReplyAt) >= since180);
        clients.push({
            businessId: b.id,
            name: b.name,
            folderLinked: !!b.folder,
            typicalReplyWorkingDays: medianWhole(answered.map((c) => c.detail.tookWorkingDays ?? 0)),
            replyBasis: { answered: answered.length, sent: sent.length },
            waitingOnClient: counts?.waitingOnClient ?? 0,
            needsUs: counts?.needsUs ?? 0,
            late: counts?.late ?? 0,
            notSentYet: counts?.notSentYet ?? 0,
            readAt: board.payload.readAt,
            folderError: board.payload.folder?.lastError ? { code: board.payload.folder.lastError.code, message: board.payload.folder.lastError.message } : null,
            redFlags: counts?.redFlags ?? null,
            // The oldest expected-by among the codes owed a doc; null with none, or before the first read.
            notSentOldestBy: counts
                ? board.payload.codes.filter((r) => r.state === "not_sent_yet").map((r) => r.expectedBy).filter((d): d is string => !!d).sort()[0] ?? null
                : null,
            // The same rows, trimmed, so All clients counts a code past due as late and names the doc.
            notSentCodes: counts
                ? board.payload.codes
                    .filter((r) => r.state === "not_sent_yet")
                    .map((r) => ({ code: r.code, outlet: r.outlet, label: r.label, expectedBy: r.expectedBy, expectedMonth: r.expectedMonth ?? null, late: r.late }))
                : null,
        });
        for (const c of counted) {
            const d = c.detail;
            const row: AcrossRow = {
                businessId: b.id,
                businessName: b.name,
                documentId: d.id,
                title: c.title,
                codes: d.codes,
                outlet: d.outlet,
                url: d.url,
                sharedAt: d.sharing.sharedAt,
                replyBy: d.replyBy,
                waitingSince: d.waitingSince,
                waitingWorkingDays: d.waitingWorkingDays,
                lateWorkingDays: d.lateWorkingDays,
                questions: d.questions ? { out: d.questions.out, back: d.questions.back } : null,
                lastClientPost: d.lastClientPost,
                redFlags: d.flags.filter((f) => f.tone === "red").map((f) => f.code),
            };
            if (d.state === "waiting_on_client" || d.state === "late") waitingOnClient.push(row);
            if (d.state === "needs_us") needsUs.push(row);
            if (d.late === true) late.push(row);
        }
    }
    const oldest = (x: AcrossRow, y: AcrossRow) => (x.waitingSince ?? "9999").localeCompare(y.waitingSince ?? "9999");
    waitingOnClient.sort(oldest);
    needsUs.sort(oldest);
    late.sort((x, y) => (y.lateWorkingDays ?? 0) - (x.lateWorkingDays ?? 0));
    return { syncedAt: ctx.sync, clients, waitingOnClient, needsUs, late };
}

/* ------------------------------------------------------------------------- *
 * The fake server
 * ------------------------------------------------------------------------- */

function clone<T>(v: T): T {
    return JSON.parse(JSON.stringify(v)) as T;
}

/**
 * Every sample call goes through here: the dev mode first (error fails, slow
 * waits 3 s), then any read that has come due, then the work, then a save.
 */
async function respond<T>(work: (ctx: Ctx) => DocsResult<T>): Promise<DocsResult<T>> {
    const mode = docsDevMode();
    await new Promise((r) => window.setTimeout(r, docsMockDelayMs()));
    if (mode === "error") return docsFailure("network");
    const ctx = ctxNow();
    settleReads(ctx);
    const result = work(ctx);
    saveStore();
    return result.ok ? { ok: true, data: clone(result.data) } : result;
}

const ok = <T>(data: T): DocsResult<T> => ({ ok: true, data });
const findBusiness = (id: string) => store.businesses.find((b) => b.id === id) ?? null;

/** Names for the other fixture businesses (devMock.ts), so a folder linked to one reads well on All clients. */
const KNOWN_NAMES: Record<string, string> = {
    "mock-list-pending": "[Mock] Not-Yet-Scored Business",
};

/**
 * The business, or a stand-in with no folder and no contract for one the
 * sample store has never heard of. `keep` adds the stand-in to the store,
 * which only linking a folder does: a read never grows All clients.
 */
function businessFor(id: string, keep = false): MockBusiness {
    const found = findBusiness(id);
    if (found) return found;
    const b: MockBusiness = { id, name: KNOWN_NAMES[id] ?? id, contract: null, folder: null };
    if (keep) store.businesses.push(b);
    return b;
}

function findDoc(id: string): { f: MockFile; b: MockBusiness } | null {
    const f = store.files.find((x) => x.id === id && !x.trashed);
    if (!f) return null;
    const b = findBusiness(f.businessId);
    if (!b?.folder || b.folder.folderId !== f.folderId) return null;
    return { f, b };
}

function docNow(f: MockFile, b: MockBusiness, ctx: Ctx): DocumentDetail {
    const readAt = b.folder ? folderReadAt(b.folder, ctx) : null;
    const detail = computeDoc(f, b, readAt, ctx).detail;
    return decorators.detail ? decorators.detail(detail) : detail;
}

/**
 * Additive fields another sample store serves on these routes (phase 2:
 * route 8's `questionList`, route 1's `write`, route 12's `answered`, and
 * the client page's doc_approved reports), registered by clientDocsWriteMock.ts. Unset, the routes
 * answer exactly as Phase 1.
 */
const decorators: {
    detail?: (d: DocumentDetail) => DocumentDetail;
    connection?: (c: DocsConnection) => DocsConnection;
    merchant?: (docs: MerchantDocument[], businessId: string) => MerchantDocument[];
    reports?: (businessId: string) => ApprovedReportLink[];
} = {};

/** A filed approved PDF as the client page's reports list carries it (B24: an OuraReport of kind doc_approved). */
export interface ApprovedReportLink {
    id: string;
    title: string;
    date: string;
    url: string | null;
    kind: "doc_approved";
    pages: null;
    /** The one line under its title, as the monthly reports have. */
    summary?: string;
}

/** The client page's doc_approved report rows on the sample; [] until the write sample registers them. */
export function mockApprovedReportLinks(businessId: string): ApprovedReportLink[] {
    return decorators.reports ? decorators.reports(businessId) : [];
}

export function registerDocsDecorators(d: typeof decorators): void {
    Object.assign(decorators, d);
}

export const docsMock = {
    getDocsConnection: () =>
        respond<DocsConnection>((ctx) => {
            const minutes = Math.max(0, Math.floor((ctx.now - Date.parse(ctx.sync)) / 60_000));
            const connection: DocsConnection = {
                status: "connected",
                actingAs: OURA_ACCOUNT,
                access: "read",
                checkedAt: ctx.sync,
                lastSyncAt: ctx.sync,
                lastSyncError: null,
                syncEveryMinutes: 5,
                detail: `Reads client folders as ${OURA_ACCOUNT}. Last read ${minutes === 0 ? "just now" : `${minutes} min ago`}.`,
            };
            return ok(decorators.connection ? decorators.connection(connection) : connection);
        }),

    getDocsFolder: (businessId: string) => respond<DocsFolder | null>((ctx) => ok(toFolder(businessFor(businessId), ctx))),

    createDocsFolder: (businessId: string) =>
        respond<DocsFolderCreated>((ctx) => {
            const b = businessFor(businessId, true);
            if (b.folder) return docsFailure("rejected", { code: "already_linked", status: 409, message: DOCS_ERROR_MESSAGE.already_linked });
            // Sample hooks, by the client's name, for the refusals the card shows.
            if (/ambiguous/i.test(b.name)) {
                const folders = ["sample-a", "sample-b"].map((id) => ({ id, url: `https://drive.google.com/drive/folders/${id}` }));
                return docsFailure("rejected", { code: "ambiguous_folder", status: 409, folders });
            }
            if (/no projects/i.test(b.name)) return docsFailure("rejected", { code: "projects_folder_missing", status: 424 });
            const folderId = `sample-${b.id.replace(/[^A-Za-z0-9]/g, "").slice(0, 20) || "folder"}`;
            b.folder = {
                folderId,
                url: `https://drive.google.com/drive/folders/${folderId}`,
                name: b.name,
                linkedAt: ctx.nowIso,
                linkedBy: sampleActor(MOCK_STAFF),
                lastReadAt: null,
                readable: true,
                lastError: null,
                pendingReadAt: new Date(ctx.now + 4000).toISOString(),
            };
            changed();
            const folder = toFolder(b, ctx);
            return folder ? ok({ ...folder, created: true }) : docsFailure("network");
        }),

    linkDocsFolder: (businessId: string, url: string) =>
        respond<DocsFolder>((ctx) => {
            const parsed = parseDriveFolderLink(url);
            if (!parsed.ok) return docsFailure("rejected", { code: "folder_url_invalid", status: 400, message: parsed.message });
            const other = store.businesses.find((x) => x.id !== businessId && x.folder?.folderId === parsed.folderId);
            if (other) {
                return docsFailure("rejected", {
                    code: "folder_linked_elsewhere",
                    status: 409,
                    message: `This folder is already linked to ${other.name}.`,
                    linkedTo: { businessId: other.id, name: other.name },
                });
            }
            // A sample hook for the 424: a folder id starting "noaccess" is one oura@ cannot open.
            if (/^noaccess/i.test(parsed.folderId)) return docsFailure("rejected", { code: "folder_not_readable", status: 424 });
            const b = businessFor(businessId, true);
            b.folder = {
                folderId: parsed.folderId,
                url: parsed.url,
                name: b.name,
                linkedAt: ctx.nowIso,
                linkedBy: sampleActor(MOCK_STAFF),
                lastReadAt: null,
                readable: true,
                lastError: null,
                pendingReadAt: new Date(ctx.now + 4000).toISOString(),
            };
            changed();
            const folder = toFolder(b, ctx);
            return folder ? ok(folder) : docsFailure("network");
        }),

    unlinkDocsFolder: (businessId: string) =>
        respond<{ unlinked: true }>(() => {
            const b = findBusiness(businessId);
            if (!b?.folder) return docsFailure("notFound", { status: 404, message: "No folder is linked." });
            b.folder = null;
            changed();
            return ok({ unlinked: true as const });
        }),

    readDocsFolderNow: (businessId: string) =>
        respond<{ queued: true }>((ctx) => {
            const b = findBusiness(businessId);
            if (!b?.folder) return docsFailure("notFound", { status: 404, message: "No folder is linked." });
            b.folder.pendingReadAt = new Date(ctx.now + 3000).toISOString();
            changed();
            return ok({ queued: true as const });
        }),

    listBusinessDocuments: (businessId: string) => respond<BusinessDocuments>((ctx) => ok(computeBoard(businessFor(businessId), ctx).payload)),

    getDocument: (documentId: string) =>
        respond<DocumentDetail>((ctx) => {
            const hit = findDoc(documentId);
            if (!hit) return docsFailure("notFound", { status: 404, message: "That document is no longer in the folder." });
            return ok(docNow(hit.f, hit.b, ctx));
        }),

    pickDocumentCodes: (documentId: string, pick: PickCodeIn) =>
        respond<StaffDocument>((ctx) => {
            const hit = findDoc(documentId);
            if (!hit) return docsFailure("notFound", { status: 404, message: "That document is no longer in the folder." });
            const { f, b } = hit;
            if ("notClientDoc" in pick) {
                f.notClientDoc = true;
                f.pick = null;
            } else {
                if (parseDocName(fileName(f, ctx)).codes.length) return docsFailure("rejected", { code: "codes_in_name", status: 409 });
                const bad: string[] = [];
                const codes: string[] = [];
                let outlet = pick.outlet?.trim() || null;
                for (const raw of pick.codes) {
                    const m = CODE_RE.exec(raw.trim());
                    if (!m || !BASE_CODE_RE.test(m[1])) {
                        bad.push(raw);
                        continue;
                    }
                    if (!codes.includes(m[1])) codes.push(m[1]);
                    if (m[2] && !outlet) outlet = m[2];
                }
                if (bad.length) return docsFailure("rejected", { code: "unknown_code", status: 400, message: `Oura doesn't know ${bad.join(", ")}.`, badCodes: bad });
                // An outlet may be its place name as well as a short code (#3973).
                if (outlet !== null && !outletOk(outlet)) return docsFailure("rejected", { code: "outlet_invalid", status: 400, message: OUTLET_INVALID_TEXT });
                f.pick = codes.length ? { codes, outlet } : null;
                f.notClientDoc = false;
            }
            changed();
            return ok(staffDoc(docNow(f, b, ctx)));
        }),

    setDocumentReplyBy: (documentId: string, replyBy: string | null) =>
        respond<StaffDocument>((ctx) => {
            const hit = findDoc(documentId);
            if (!hit) return docsFailure("notFound", { status: 404, message: "That document is no longer in the folder." });
            if (replyBy !== null) {
                if (!/^\d{4}-\d{2}-\d{2}$/.test(replyBy)) return docsFailure("rejected", { code: "reply_by_in_past", status: 400, message: "That isn't a date." });
                if (replyBy < ctx.today) return docsFailure("rejected", { code: "reply_by_in_past", status: 400 });
            }
            hit.f.replyBy = replyBy;
            changed();
            return ok(staffDoc(docNow(hit.f, hit.b, ctx)));
        }),

    /** Mirrors POST/DELETE …/documents/{id}/done: a `doneOn` after today is refused, as the server does (422). */
    markDocumentDone: (documentId: string, done: boolean, doneOn: string | null = null) =>
        respond<StaffDocument>((ctx) => {
            const hit = findDoc(documentId);
            if (!hit) return docsFailure("notFound", { status: 404, message: "That document is no longer in the folder." });
            if (done && doneOn !== null) {
                if (!/^\d{4}-\d{2}-\d{2}$/.test(doneOn)) return docsFailure("rejected", { status: 422, message: "That isn't a date." });
                if (doneOn > ctx.today) return docsFailure("rejected", { code: "done_on_future", status: 422 });
            }
            // The work day at noon Malaysia time (04:00 UTC), or now when no day is given.
            hit.f.done = done ? { at: doneOn ? `${doneOn}T04:00:00.000Z` : ctx.nowIso, by: MOCK_STAFF } : null;
            changed();
            return ok(staffDoc(docNow(hit.f, hit.b, ctx)));
        }),

    getDocsAcrossClients: () => respond<DocsAcrossClients>((ctx) => ok(computeAcross(ctx))),
};

/* ------------------------------------------------------------------------- *
 * Change listeners and notices
 * ------------------------------------------------------------------------- */

let onChanged: (() => void) | null = null;

/** clientDocs.ts registers its change broadcast here, so the dev tools refresh open screens. */
export function setDocsMockListener(fn: () => void): void {
    onChanged = fn;
}
function changed(): void {
    onChanged?.();
}

/** A bell entry the documents store raises, for the notifications store to keep. */
export interface DocsNotice {
    audience: "client" | "staff";
    businessId: string;
    title: string;
    body: string;
    link: string;
}
const noticeListeners = new Set<(n: DocsNotice) => void>();

/** Be told when a sample event would ring a bell (a doc shared, a client answer). Returns the unsubscribe. */
export function onDocsNotice(fn: (n: DocsNotice) => void): () => void {
    noticeListeners.add(fn);
    return () => {
        noticeListeners.delete(fn);
    };
}
function notice(n: DocsNotice): void {
    noticeListeners.forEach((fn) => fn(n));
}

/* ------------------------------------------------------------------------- *
 * Dev tools: simulate what the 5-minute sync would read
 * ------------------------------------------------------------------------- */

const CLIENT_FALLBACK: MockAuthor = { email: "siewling@nyonyatable.com", name: "Siew Ling" };
const OURA: MockAuthor = { email: OURA_ACCOUNT, name: "Angel" };

function clientOf(f: MockFile, b: MockBusiness): MockAuthor {
    const p = f.permissions.find((x) => x.type === "user" && x.email && !isOurEmail(x.email));
    if (p?.email) return { email: p.email, name: p.name ?? p.email };
    return b.id === "nyonya-table" ? CLIENT_FALLBACK : { email: `owner@${b.id}.example`, name: b.name };
}

/**
 * How the client's bell opens a share: "A document to review" only when
 * something is actually asked on it.
 *
 * With no questions, the client's own Decisions page files the doc under
 * "Nothing to answer", so a bell asking them to review it contradicts the page
 * it links to (#4051). With the count not read yet, neither claim is safe, so
 * it says only that the doc is there.
 */
export function sharedNoticeLead(questionsOut: number | null): string {
    if (questionsOut === null) return "A new document";
    return questionsOut > 0 ? "A document to review" : "A document to read";
}

function simulate(documentId: string, act: (f: MockFile, b: MockBusiness, d: DocumentDetail, ctx: Ctx) => string): string {
    const hit = findDoc(documentId);
    if (!hit) return `No document ${documentId} in a linked folder.`;
    const ctx = ctxNow();
    const d = docNow(hit.f, hit.b, ctx);
    const said = act(hit.f, hit.b, d, ctx);
    saveStore();
    changed();
    return said;
}

function threadOf(f: MockFile, id: string): MockThread | null {
    return f.threads?.find((t) => t.id === id) ?? null;
}

function titleFor(f: MockFile, ctx: Ctx): string {
    const name = fileName(f, ctx);
    const p = parseDocName(name);
    return titleOf(f.pageTitle, p.docType, p.codes, p.outlet, name);
}

const docsSim = {
    clientReply(documentId: string, text = "Yes, that works for us.") {
        return simulate(documentId, (f, b, d, ctx) => {
            const target = d.threads.find((t) => t.kind === "question" && (t.state === "waiting_on_client" || t.state === "late"));
            const t = target && threadOf(f, target.id);
            if (!t) return "Nothing is waiting on the client in this doc.";
            const who = clientOf(f, b);
            t.replies.push({ id: nextId("rep"), by: who, text, at: ctx.nowIso, action: "reply" });
            f.modifiedAt = ctx.nowIso;
            notice({ audience: "staff", businessId: b.id, title: `${who.name} answered in ${titleFor(f, ctx)}`, body: trimTo(text, 200), link: `/oura/businesses/${b.id}/reviews?doc=${f.id}` });
            return `${who.name} replied on "${trimTo(t.text, 60)}".`;
        });
    },
    ourReply(documentId: string, text = "Thank you. We have updated the doc.") {
        return simulate(documentId, (f, _b, d, ctx) => {
            const target = d.threads.find((t) => t.state === "needs_us");
            const t = target && threadOf(f, target.id);
            if (!t) return "Nothing in this doc needs us.";
            t.replies.push({ id: nextId("rep"), by: OURA, text, at: ctx.nowIso, action: "reply" });
            f.modifiedAt = ctx.nowIso;
            return `Angel replied on "${trimTo(t.text, 60)}".`;
        });
    },
    approve(documentId: string) {
        return simulate(documentId, (f, b, d, ctx) => {
            const target = d.threads.find((t) => t.kind === "question" && !t.resolved);
            const t = target && threadOf(f, target.id);
            if (!t) return "No open question in this doc.";
            const who = clientOf(f, b);
            t.replies.push({ id: nextId("rep"), by: who, text: "", at: ctx.nowIso, action: "resolve" });
            notice({ audience: "staff", businessId: b.id, title: `${who.name} resolved a question in ${titleFor(f, ctx)}`, body: trimTo(t.text, 200), link: `/oura/businesses/${b.id}/reviews?doc=${f.id}` });
            return `${who.name} resolved "${trimTo(t.text, 60)}".`;
        });
    },
    share(documentId: string, email = CLIENT_FALLBACK.email, name = CLIENT_FALLBACK.name) {
        return simulate(documentId, (f, b, d, ctx) => {
            if (!f.permissions.some((p) => p.email === email)) f.permissions.push({ type: "user", email, name, role: "commenter" });
            if (!f.sharedAt) {
                f.sharedAt = ctx.nowIso;
                // Merchant words: the title without its code brackets, as the merchant list shows it.
                const plain = titleFor(f, ctx).split("\n")[0].replace(/\s*\((?:[A-Z]\d+(?:-[A-Z]{2,4})?[\s,]*)+\)\s*$/, "").trim() || titleFor(f, ctx);
                // "to review" ONLY WHEN SOMETHING IS ASKED. With no questions
                // on it the client's own page files the doc under "Nothing to
                // answer", so a bell that asks them to review it -- and gives
                // them a reply-by -- contradicts the page it links to (#4051).
                notice({ audience: "client", businessId: b.id, title: `${sharedNoticeLead(d.questions?.out ?? null)}: ${plain}`, body: "", link: `/oura/decisions?doc=${f.id}` });
            }
            return `Shared with ${email}, comment-only.`;
        });
    },
    unshare(documentId: string) {
        return simulate(documentId, (f) => {
            f.permissions = f.permissions.filter((p) => p.type === "anyone" || !p.email || isOurEmail(p.email) || p.role === "owner");
            f.sharedAt = null;
            return "The client's access is removed.";
        });
    },
    list(businessId = "nyonya-table") {
        const ctx = ctxNow();
        const b = findBusiness(businessId);
        if (!b) return [];
        return computeBoard(b, ctx).computed.map((c) => ({ id: c.detail.id, name: c.detail.name, state: c.detail.state, late: c.detail.late, codes: joinCodes(c.detail.codes) }));
    },
    mode(m: DocsDevMode) {
        try {
            window.localStorage.setItem(MODE_KEY, m);
        } catch {
            /* ignore */
        }
        return `Docs mode: ${m}.`;
    },
};

/* ------------------------------------------------------------------------- *
 * The seam the write sample (clientDocsWriteMock.ts) works through
 * ------------------------------------------------------------------------- */

/**
 * Read and change this store from the write sample, so a phase 2 write lands
 * in the same Drive-shaped data Phase 1 computes every state from. Times
 * written through it are ISO instants. `commit()` saves and tells every open
 * screen.
 */
export const docsStoreApi = {
    /** The person a write records: the signed-in person (sampleActor), else the seed's Angel. */
    get staffName(): string {
        return sampleActor(MOCK_STAFF);
    },
    /** oura@, as the sample's posts are written. */
    ouraAuthor: (name = sampleActor(MOCK_STAFF)): MockAuthor => ({ email: OURA_ACCOUNT, name }),
    nowIso: () => ctxNow().nowIso,
    today: () => ctxNow().today,
    /** Settle any read that has come due; true when something moved. */
    settle: () => settleReads(ctxNow()),
    business: (id: string): MockBusiness | null => findBusiness(id),
    files: (businessId: string): MockFile[] => {
        const b = findBusiness(businessId);
        return b ? folderFiles(b) : [];
    },
    findDoc,
    nextId,
    /** A new file in a business's linked folder (Create, B12). */
    addFile: (f: MockFile): void => {
        store.files.push(f);
    },
    /** The document as route 8 answers it now. */
    detail: (f: MockFile, b: MockBusiness): DocumentDetail => clone(docNow(f, b, ctxNow())),
    /** The file name as it stands, its date filled in. */
    fileName: (f: MockFile): string => fileName(f, ctxNow()),
    title: (f: MockFile): string => titleFor(f, ctxNow()),
    board: (b: MockBusiness): BusinessDocuments => clone(computeBoard(b, ctxNow()).payload),
    merchantDocs: (businessId: string): MerchantDocument[] | undefined => mockMerchantDocuments(businessId),
    clientOf,
    notice,
    /** Save without telling the screens (a read that settled a pending folder read). */
    save: (): void => saveStore(),
    /** Save and tell every open screen: after a write. */
    commit: (): void => {
        saveStore();
        changed();
    },
};

if (typeof window !== "undefined" && DOCS_SAMPLE_SERVES) {
    const w = window as unknown as { __ouraResetDocs?: () => void; __ouraDocsSim?: typeof docsSim };
    w.__ouraResetDocs = resetDocsStore;
    w.__ouraDocsSim = docsSim;
}

/* ------------------------------------------------------------------------- *
 * Fixtures (map_stories.md section 2)
 * ------------------------------------------------------------------------- */

const SIEW: MockAuthor = { email: "siewling@nyonyatable.com", name: "Siew Ling" };
const NOREPLY: MockAuthor = { email: "no-reply@ourteam.ai", name: "ourteam" };
const DANA: MockAuthor = { email: "dana@ourteam.ai", name: "Dana" };
const DRLIM: MockAuthor = { email: "drlim@riversidedental.my", name: "Dr Lim" };
const MAYA: MockAuthor = { email: "maya@crawlfailed.example", name: "Maya" };

/** The catalogue plan the sample clients bought, as the demo engagement lists it (clientReview.ts DEMO_ENGAGEMENT). */
const PLAN: MockDeliverable[] = [
    { code: "A3", label: "Let AI read your site", months: [1] },
    { code: "S3", label: "Technical crawl, broken pages and dead links fixed", months: [1] },
    { code: "A1", label: "Your 50 questions locked and scored on day one", months: [1] },
    { code: "S5", label: "Search Console set up, baseline recorded", months: [1] },
    { code: "S4", label: "Your name, address and contact corrected across the web", months: [1] },
    { code: "S6", label: "Pages linked together", months: [1] },
    { code: "S7", label: "Pages load fast", months: [1] },
    { code: "B1", label: "Pages written and published", months: [1] },
    { code: "S1", label: "Existing pages labelled", months: [1] },
    { code: "A2", label: "Business and page facts tagged for AI", months: [1] },
    { code: "S2", label: "Google Business Profile, every outlet", months: [1] },
    { code: "A4", label: "Handover Scorecard and call", months: [1] },
    { code: "G1", label: "New pages this month", months: [2, 3, 4, 5] },
    { code: "G2", label: "Site health sweep", months: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12] },
    { code: "G3", label: "Your scoreboard", months: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12] },
    { code: "G4", label: "Competitor watch", months: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12] },
    { code: "G5", label: "1 small change", months: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12] },
    { code: "G6", label: "Your monthly snapshot", months: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12] },
    { code: "G7", label: "AI visibility check", months: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12] },
    { code: "G8", label: "Term review", months: [6, 12] },
];

const GDOC = "application/vnd.google-apps.document";
const WORD = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
const PDF = "application/pdf";
const SHEET = "application/vnd.google-apps.spreadsheet";

const LONG_PHRASE = "Menu, Prices, Opening Hours, Private Dining, Catering, Set Menus, Tagged for Google and AI Answers";
const LONG_REPLY =
    "Most of it is right, thank you. A few changes before this goes live. The nyonya laksa is RM26 now, not RM24, and the set lunch is RM42 from 1 October. " +
    "Please take the Deepavali menu off, we only run it for 2 weeks and it confuses people the rest of the year. The private room seats 12 but we can squeeze 14 for birthdays, so please say up to 14. " +
    "Opening hours are right for Bukit Bintang but Mont Kiara now closes at 9pm on weekdays. We no longer do delivery ourselves, only through GrabFood and foodpanda, so the delivery line should say that. " +
    "Can we also add that we are halal-friendly, so no pork?";

function seedStore(): DocsStore {
    let seq = 0;
    const id = (p: string) => `${p}_${++seq}`;
    const thread = (by: MockAuthor, text: string, when: Stamp, replies: Array<[MockAuthor, string, Stamp, MockReply["action"]?]> = [], quoted: string | null = null): MockThread => ({
        id: id("thr"),
        by,
        text,
        quoted,
        at: when,
        replies: replies.map(([rb, rt, ra, action]) => ({ id: id("rep"), by: rb, text: rt, at: ra, action: action ?? "reply" })),
    });
    const owner = (email: string, name: string): MockPermission => ({ type: "user", email, name, role: "owner" });
    const commenter = (a: MockAuthor): MockPermission => ({ type: "user", email: a.email, name: a.name, role: "commenter" });
    const writer = (a: MockAuthor): MockPermission => ({ type: "user", email: a.email, name: a.name, role: "writer" });
    const file = (f: Partial<MockFile> & Pick<MockFile, "businessId" | "folderId" | "name" | "mimeType">): MockFile => ({
        id: id("doc"),
        driveFileId: `1${f.businessId.replace(/[^a-z]/g, "").slice(0, 6)}${(seq * 7919).toString(36)}Xq3vT9bK2mZ8pL4wR6`,
        nameDate: null,
        pageTitle: null,
        owner: OURA_ACCOUNT,
        createdAt: "@wd:-10",
        modifiedAt: "@wd:-1T15:00",
        trashed: false,
        permissions: [owner(OURA_ACCOUNT, "Angel")],
        sharedAt: null,
        pick: null,
        notClientDoc: false,
        replyBy: null,
        threads: [],
        commentsError: false,
        ...f,
    });

    const NYO = "nyonya-table";
    const NYO_FOLDER = "1NyonyaTableClientFolder7hQ2cW";
    const nyo = (f: Partial<MockFile> & Pick<MockFile, "name" | "mimeType">) => file({ businessId: NYO, folderId: NYO_FOLDER, ...f });
    const ours = [owner(OURA_ACCOUNT, "Angel"), writer(DANA)];

    const files: MockFile[] = [
        // F1: shared, comment-only, late. 7 questions: 3 answered (1 "AGREED", unresolved), 1 resolved, 3 open with our post last.
        // Also 4 of our notes, 2 placeholders, 1 raised by the client with no reply.
        nyo({
            id: "doc_f1_answer_plan",
            name: "(S1-S3-B1-G4) Nyonya Table_Answer Plan_{S}_1.0",
            nameDate: "@wd:-9",
            pageTitle: "Answer Plan (S1, S3, B1, G4)\nNyonya Table · v1.0",
            mimeType: GDOC,
            createdAt: "@wd:-9T09:30",
            modifiedAt: "@wd:-2T20:10",
            permissions: [...ours, commenter(SIEW)],
            sharedAt: "@wd:-8T10:00",
            threads: [
                thread(OURA, "Section 3 lists the 50 questions we track for you each month.", "@wd:-9T09:35"),
                thread(NOREPLY, "Please confirm your 3 signature dishes for the homepage: ayam buah keluak, nyonya laksa and chap chye.", "@wd:-9T09:40", [
                    [SIEW, "Yes, those 3. Add kueh pie tee as a 4th if there is room.", "@wd:-6T14:20"],
                ]),
                thread(NOREPLY, "We suggest watching these 5 rivals each month: Candlenut, Violet Oon, Godmama, Baba Chews and Kim Choo. Would you rather we watched someone else?", "@wd:-9T09:42", [
                    [SIEW, "Swap Kim Choo for Blue Ginger. Kim Choo is a shop, not a restaurant.", "@wd:-5T11:05"],
                ]),
                thread(OURA, "Reply AGREED if we can say the private room seats 12, with a minimum spend of RM800.", "@wd:-9T09:45", [[SIEW, "AGREED", "@wd:-4T16:30"]]),
                thread(OURA, "Please approve the new opening paragraph in section 1.", "@wd:-9T09:47", [[OURA, "Siew Ling approved this on our call.", "@wd:-5T10:00", "resolve"]]),
                thread(OURA, "The page titles below follow the names on your menu.", "@wd:-9T09:48"),
                thread(OURA, "Would you like prices on the set lunch page, or kept off the site?", "@wd:-9T09:50", [[OURA, "A quick nudge on this one. A yes or no is enough.", "@wd:-3T09:15"]]),
                // C05: a file-only question, so an empty send asks for the file, not for words.
                { ...thread(OURA, "Can you send 3 photos of the private room, in daylight, with the table set?", "@wd:-9T09:52"), askKind: "file" },
                thread(OURA, "Photos marked in yellow are placeholders until yours arrive.", "@wd:-9T09:53"),
                thread(OURA, "Is there anything in this plan you would like changed before we start writing?", "@wd:-9T09:55"),
                thread(OURA, "This plan covers your first 2 months.", "@wd:-9T09:56"),
                thread(NOREPLY, "Please leave any comments or suggestions on this page.", "@wd:-9T09:57"),
                thread(NOREPLY, "Feel free to comment anywhere in this document.", "@wd:-9T09:58"),
                thread(SIEW, "We close on Mondays from next month. Can the hours on page 2 say that?", "@wd:-2T20:10", [], "Open daily, 11.30am to 9.30pm"),
            ],
        }),
        // F2: shared, waiting, not late.
        nyo({
            id: "doc_f2_gbp_mont_kiara",
            name: "(S2) Nyonya Table_GBP Pack_Mont Kiara_{S}_1.0",
            nameDate: "@wd:-3",
            pageTitle: "GBP Pack Mont Kiara (S2-MK)\nNyonya Table · v1.0",
            mimeType: GDOC,
            createdAt: "@wd:-3T11:00",
            modifiedAt: "@wd:-2T09:30",
            permissions: [...ours, commenter(SIEW)],
            sharedAt: "@wd:-2T09:30",
            threads: [
                thread(OURA, "Please confirm Restaurant as the main category, with Peranakan restaurant and Caterer added.", "@wd:-3T11:10"),
                thread(OURA, "Are the Mont Kiara hours the same as Bukit Bintang: 11.30am to 9.30pm, closed Monday?", "@wd:-3T11:12"),
                thread(OURA, "Would you like the set lunch price shown in your Google posts?", "@wd:-3T11:14"),
                thread(OURA, "Please confirm this outlet's phone number: 03-6201 8812.", "@wd:-3T11:16"),
                thread(OURA, "Please leave any comments or suggestions.", "@wd:-3T11:17"),
            ],
        }),
        // F3: shared, every question resolved: Approved, past its reply-by but not late.
        nyo({
            id: "doc_f3_gbp_bukit_bintang",
            name: "(S2) Nyonya Table_GBP Pack_Bukit Bintang_{S}_1.0",
            nameDate: "@wd:-12",
            pageTitle: "GBP Pack Bukit Bintang (S2-BB)\nNyonya Table · v1.0",
            mimeType: GDOC,
            createdAt: "@wd:-12T10:00",
            modifiedAt: "@wd:-7T12:00",
            permissions: [...ours, commenter(SIEW)],
            sharedAt: "@wd:-11T10:00",
            threads: [
                thread(OURA, "Please confirm Restaurant as the main category, with Peranakan restaurant added.", "@wd:-12T10:10", [
                    [SIEW, "Yes.", "@wd:-8T09:40"],
                    [SIEW, "", "@wd:-8T09:41", "resolve"],
                ]),
                thread(OURA, "Would you like the private room listed as a separate service?", "@wd:-12T10:12", [
                    [SIEW, "Yes please, with the minimum spend.", "@wd:-8T09:45"],
                    [OURA, "Done. It now reads: private dining, 12 guests, minimum RM800.", "@wd:-7T11:30"],
                    [OURA, "", "@wd:-7T11:31", "resolve"],
                ]),
                thread(OURA, "Please approve the 4 photos in section 3 for the listing.", "@wd:-12T10:14", [[SIEW, "", "@wd:-8T09:50", "resolve"]]),
            ],
        }),
        // F4: the old name format, still read. Shared, waiting.
        nyo({
            id: "doc_f4_business_details",
            name: "W2 (S4) Nyonya Table_Business Details_{D}_V1",
            nameDate: "@wd:-4",
            pageTitle: "Business Details\nNyonya Table",
            mimeType: GDOC,
            owner: NOREPLY.email,
            createdAt: "@wd:-4T11:00",
            modifiedAt: "@wd:-4T15:00",
            permissions: [owner(NOREPLY.email, NOREPLY.name), writer(OURA), commenter(SIEW)],
            sharedAt: "@wd:-4T15:00",
            threads: [
                thread(NOREPLY, "Please confirm the business name exactly as you write it: Nyonya Table, or Nyonya Table Restaurant?", "@wd:-4T11:20"),
                thread(NOREPLY, "Which email should customers use: hello@nyonyatable.com or bookings@nyonyatable.com?", "@wd:-4T11:22"),
                thread(NOREPLY, "Please confirm the Bukit Bintang address: 12 Jalan Alor, 50200 Kuala Lumpur.", "@wd:-4T11:24"),
            ],
        }),
        // F5: a Word copy with no code. Its comments still read. Not shared.
        nyo({
            id: "doc_f5_word_copy",
            name: "Nyonya Table_Answer Plan_{D}.docx",
            nameDate: "@wd:-10",
            mimeType: WORD,
            owner: DANA.email,
            createdAt: "@wd:-10T16:00",
            modifiedAt: "@wd:-10T16:30",
            permissions: [owner(DANA.email, DANA.name), writer(OURA)],
            threads: [
                thread(OURA, "Please confirm your 3 signature dishes for the homepage.", "@wd:-10T16:10"),
                thread(OURA, "Would you like prices on the set lunch page?", "@wd:-10T16:12"),
                thread(DANA, "Draft copy. The Google Doc is the one we share.", "@wd:-10T16:20"),
            ],
        }),
        // F6: link sharing on (anyone, editor), also shared to the client. 2 internal notes the client can read.
        nyo({
            id: "doc_f6_copy_pack",
            name: "(B1) Nyonya Table_Copy Pack_{S}_1.0",
            nameDate: "@wd:-5",
            pageTitle: "Copy Pack (B1)\nNyonya Table · v1.0",
            mimeType: GDOC,
            createdAt: "@wd:-5T10:00",
            modifiedAt: "@wd:-3T13:40",
            permissions: [...ours, commenter(SIEW), { type: "anyone", email: null, name: null, role: "writer" }],
            sharedAt: "@wd:-4T10:30",
            threads: [
                thread(DANA, "Check the laksa price with Siew Ling before this goes out. The menu says RM26, the site RM24.", "@wd:-5T10:20"),
                thread(OURA, "Please approve the homepage intro in section 1.", "@wd:-5T10:30"),
                thread(OURA, "Reply AGREED if the set lunch copy can go live as written.", "@wd:-5T10:32", [
                    [SIEW, "Can it say 3 courses instead of three courses? Otherwise AGREED.", "@wd:-3T13:40"],
                ]),
                thread(OURA, "Do you want the catering page to name your corporate clients?", "@wd:-5T10:34"),
                thread(DANA, "Old tagline kept below for reference. Delete before sharing.", "@wd:-5T10:40"),
            ],
        }),
        // F7: exists, not shared with the client.
        nyo({
            id: "doc_f7_copy_pack_home",
            name: "(B1) Nyonya Table_Copy Pack Home_{S}_1.0",
            nameDate: "@wd:-2",
            pageTitle: "Copy Pack Home (B1)\nNyonya Table · v1.0",
            mimeType: GDOC,
            createdAt: "@wd:-2T10:00",
            modifiedAt: "@wd:-1T17:00",
            permissions: ours,
            threads: [
                thread(OURA, "Please approve the new homepage headline.", "@wd:-2T10:10"),
                thread(OURA, "Would you like the opening hours in the page footer?", "@wd:-2T10:12"),
            ],
        }),
        // F8: the name says G4, the page title says G1. Filed under G4. Not shared yet.
        nyo({
            id: "doc_f8_competitor_watch",
            name: "(G4) Nyonya Table_Competitor Watch_{S}_1.0",
            nameDate: "@wd:-2",
            pageTitle: "Competitor Watch (G1)\nNyonya Table · v1.0",
            mimeType: GDOC,
            createdAt: "@wd:-2T14:00",
            modifiedAt: "@wd:-2T14:30",
            permissions: ours,
            threads: [
                thread(OURA, "Would you like Godmama added to next month's watch list?", "@wd:-2T14:10"),
                thread(OURA, "3 rivals added set lunch pages this month.", "@wd:-2T14:12"),
            ],
        }),
        // F9: X2 is not in the contract (every catalogue code is). Not shared.
        nyo({
            id: "doc_f9_loyalty_card",
            name: "(X2) Nyonya Table_Loyalty Card Page_{S}_1.0",
            nameDate: "@wd:-1",
            pageTitle: "Loyalty Card Page (X2)\nNyonya Table · v1.0",
            mimeType: GDOC,
            createdAt: "@wd:-1T11:00",
            modifiedAt: "@wd:-1T11:20",
            permissions: ours,
            threads: [thread(OURA, "Please confirm the loyalty card rules: 10 stamps for a free set lunch.", "@wd:-1T11:10")],
        }),
        // F10: the timeline PDF and the master tracker Sheet. No code; Oura does not read comments there.
        nyo({
            id: "doc_f10_timeline_pdf",
            name: "ourteam_Nyonya Table_Execution Timeline_v1.0_{d}.pdf",
            nameDate: "@cal:-40",
            mimeType: PDF,
            createdAt: "@cal:-40",
            modifiedAt: "@cal:-40",
            threads: null,
        }),
        nyo({
            id: "doc_f10_master_tracker",
            name: "Nyonya Table master tracker",
            mimeType: SHEET,
            createdAt: "@cal:-42",
            modifiedAt: "@wd:-1T18:00",
            permissions: ours,
            threads: null,
        }),
        // F11: trashed. Never listed.
        nyo({
            id: "doc_f11_trashed",
            name: "Copy of ourteam_Nyonya Table_Answer Plan (S1, S3, B1, G4)_v1.0_{d}",
            nameDate: "@wd:-9",
            mimeType: GDOC,
            trashed: true,
            threads: [],
        }),
        // F12: a 140-character name, and 600 characters of the client's last words.
        nyo({
            id: "doc_f12_long_name",
            name: `ourteam_Nyonya Table_{fit140:${LONG_PHRASE}} (A2)_v1.0_{d}`,
            nameDate: "@wd:-6",
            mimeType: GDOC,
            createdAt: "@wd:-6T09:00",
            modifiedAt: "@wd:-4T21:15",
            permissions: [...ours, commenter(SIEW)],
            sharedAt: "@wd:-6T10:00",
            threads: [
                thread(OURA, "Please check the menu and prices we tagged for AI answers in section 2. Is anything out of date?", "@wd:-6T09:30", [[SIEW, LONG_REPLY, "@wd:-4T21:15"]]),
            ],
        }),
        // F13: shared to the client's email as an editor.
        nyo({
            id: "doc_f13_page_titles",
            name: "(S1) Nyonya Table_Page Titles Review_{S}_1.0",
            nameDate: "@wd:-4",
            pageTitle: "Page Titles Review (S1)\nNyonya Table · v1.0",
            mimeType: GDOC,
            createdAt: "@wd:-4T09:00",
            modifiedAt: "@wd:-1T12:00",
            permissions: [...ours, writer(SIEW)],
            sharedAt: "@wd:-3T09:00",
            threads: [
                thread(OURA, "Please approve the 10 new page titles in the table on page 2.", "@wd:-4T09:10", [
                    [SIEW, "Approved, thank you.", "@wd:-1T12:00"],
                    [SIEW, "", "@wd:-1T12:01", "resolve"],
                ]),
                thread(OURA, "Would you like Peranakan or Nyonya first in the homepage title?", "@wd:-4T09:12"),
            ],
        }),
    ];

    // Riverside Dental: 2 docs waiting on the client, the oldest shared 12 working days ago. Never replied.
    // Its Answer Plan keeps the older space-separated codes, "(S1 S3 B1 G4)", to show names written
    // before the comma rule still read; its drawer offers the decided name.
    const RIV = "new-clinic";
    const RIV_FOLDER = "1RiversideDentalFolder4kP9sN";
    const riv = (f: Partial<MockFile> & Pick<MockFile, "name" | "mimeType">) => file({ businessId: RIV, folderId: RIV_FOLDER, ...f });
    files.push(
        riv({
            id: "doc_riv_answer_plan",
            name: "ourteam_Riverside Dental_Answer Plan (S1 S3 B1 G4)_v1.0_{d}",
            nameDate: "@wd:-13",
            pageTitle: "Answer Plan (S1 S3 B1 G4)\nRiverside Dental · v1.0",
            mimeType: GDOC,
            createdAt: "@wd:-13T10:00",
            modifiedAt: "@wd:-5T10:00",
            permissions: [owner(OURA_ACCOUNT, "Angel"), commenter(DRLIM)],
            sharedAt: "@wd:-12T10:00",
            threads: [
                thread(OURA, "Please confirm the 3 treatments to lead with: Invisalign, dental implants and scaling.", "@wd:-13T10:10", [
                    [OURA, "A reminder on these questions. A short reply is enough.", "@wd:-5T10:00"],
                ]),
                thread(OURA, "Would you like price ranges on the implants page, or no prices?", "@wd:-13T10:12"),
                thread(OURA, "Is Dr Lim's registration number on the team page correct?", "@wd:-13T10:14"),
                thread(OURA, "Please confirm your Saturday hours: 9am to 1pm.", "@wd:-13T10:16"),
                thread(OURA, "Please leave any comments or suggestions.", "@wd:-13T10:18"),
            ],
        }),
        riv({
            id: "doc_riv_gbp_pack",
            name: "ourteam_Riverside Dental_GBP Pack (S2)_v1.0_{d}",
            nameDate: "@wd:-3",
            pageTitle: "GBP Pack (S2)\nRiverside Dental · v1.0",
            mimeType: GDOC,
            createdAt: "@wd:-3T10:00",
            modifiedAt: "@wd:-3T11:00",
            permissions: [owner(OURA_ACCOUNT, "Angel"), commenter(DRLIM)],
            sharedAt: "@wd:-3T11:00",
            threads: [
                thread(OURA, "Please confirm Dental clinic as the main category, with Cosmetic dentist and Orthodontist added.", "@wd:-3T10:20"),
                thread(OURA, "Would you like your Saturday hours shown on Google?", "@wd:-3T10:22"),
            ],
        }),
    );

    // A business whose folder read fails: oura@ lost access. The last good read stays on the board.
    const CF = "mock-list-crawl-failed";
    const CF_FOLDER = "1CrawlFailedClientFolder8dJ3";
    const cf = (f: Partial<MockFile> & Pick<MockFile, "name" | "mimeType">) => file({ businessId: CF, folderId: CF_FOLDER, ...f });
    files.push(
        cf({
            id: "doc_cf_answer_plan",
            name: "ourteam_Crawl-Failed Business_Answer Plan (S1, S3, B1, G4)_v1.0_{d}",
            nameDate: "@wd:-4",
            pageTitle: "Answer Plan (S1, S3, B1, G4)",
            mimeType: GDOC,
            createdAt: "@wd:-4T10:00",
            permissions: [owner(OURA_ACCOUNT, "Angel"), commenter(MAYA)],
            sharedAt: "@wd:-3T10:00",
            threads: [
                thread(OURA, "Please confirm the 3 services to lead with.", "@wd:-4T10:10"),
                thread(OURA, "Would you like prices on the website?", "@wd:-4T10:12"),
            ],
        }),
        cf({
            id: "doc_cf_business_details",
            name: "ourteam_Crawl-Failed Business_Business Details (S4)_v1.0_{d}",
            nameDate: "@wd:-6",
            pageTitle: "Business Details (S4)",
            mimeType: GDOC,
            createdAt: "@wd:-6T10:00",
            permissions: [owner(OURA_ACCOUNT, "Angel"), commenter(MAYA)],
            sharedAt: "@wd:-5T10:00",
            commentsError: true,
            threads: [
                thread(OURA, "Please confirm the address as it should appear everywhere.", "@wd:-6T10:10", [[MAYA, "Use the Jalan Ampang address, not the old one.", "@wd:-3T15:00"]]),
            ],
        }),
    );

    const folder = (folderId: string, name: string, lastReadAt: Stamp | null, extra: Partial<MockFolder> = {}): MockFolder => ({
        folderId,
        url: `https://drive.google.com/drive/folders/${folderId}`,
        name,
        linkedAt: "@cal:-30",
        linkedBy: MOCK_STAFF,
        lastReadAt,
        readable: true,
        lastError: null,
        pendingReadAt: null,
        ...extra,
    });

    // Contract starts stay relative to today, so each client sits at the same
    // point of its plan whenever the preview opens: Nyonya Table and the
    // Crawl-Failed business in month 2 (G1 owed, Foundation codes past due),
    // Riverside Dental and The Pilates Collective inside the Foundation build.
    const businesses: MockBusiness[] = [
        { id: NYO, name: "Nyonya Table", contract: { startedAt: "@cal:-44", termMonths: 12, outlets: 1, deliverables: PLAN }, folder: folder(NYO_FOLDER, "Nyonya Table", "@sync") },
        { id: "pilates-collective", name: "The Pilates Collective", contract: { startedAt: "@cal:-10", termMonths: 12, outlets: 1, deliverables: PLAN }, folder: folder("1PilatesCollectiveFolder2mR7", "The Pilates Collective", "@sync") },
        { id: RIV, name: "Riverside Dental", contract: { startedAt: "@cal:-20", termMonths: 12, outlets: 1, deliverables: PLAN }, folder: folder(RIV_FOLDER, "Riverside Dental", "@sync") },
        { id: "mock-list-scored", name: "[Mock] Scored Business", contract: { startedAt: "@cal:-10", termMonths: 12, outlets: 1, deliverables: PLAN }, folder: null },
        {
            id: CF,
            name: "[Mock] Crawl-Failed Business",
            contract: { startedAt: "@cal:-40", termMonths: 6, outlets: 1, deliverables: PLAN },
            folder: folder(CF_FOLDER, "Crawl-Failed Business", "@wd:-2T16:40", {
                readable: false,
                lastError: { code: "folder_not_readable", message: DOCS_ERROR_MESSAGE.folder_not_readable, at: "@sync" },
            }),
        },
    ];

    return { version: 1, rev: FIXTURE_REV, businesses, files, seq: seq + 100 };
}

store = loadStore();

// Another tab wrote the sample store (a share, an answer): read it again and
// tell the open screens, so All clients and the board move without a reload.
if (typeof window !== "undefined" && DOCS_SAMPLE_SERVES) {
    window.addEventListener("storage", (e) => {
        if (e.key !== STORE_KEY) return;
        store = loadStore();
        changed();
    });
}
